import type { SubscriptionState, SubscriptionStatus } from "@/api/payments";

const OPEN_PATHS = ["/payment", "/settings", "/help", "/legal"] as const;

/**
 * Paid pages that live under an open prefix (RD-07: the redesign moves Rules into
 * Settings & profile). `/settings` is open so an unpaid account can still sign out and
 * delete itself; that must not open the paid pages filed beside it. Checked first.
 */
const PAID_UNDER_OPEN = ["/settings/rules"] as const;

export interface SubscriptionNotice {
  title: string;
  body: string;
}

const under = (pathname: string, path: string) =>
  pathname === path || pathname.startsWith(`${path}/`);

export function isSubscriptionOpenPath(pathname: string): boolean {
  if (PAID_UNDER_OPEN.some((path) => under(pathname, path))) return false;
  return OPEN_PATHS.some((path) => under(pathname, path));
}

export function subscriptionRedirectTarget(
  pathname: string,
  search: string,
  hash: string,
  state: SubscriptionState,
): string | null {
  // Access is billing OR the exemption, and this gate is the only reader that
  // wants the OR. Everything else reads `is_entitled` as "has a subscription".
  const hasAccess = state.is_entitled || state.is_exempt;
  if (!state.gating_enabled || hasAccess || isSubscriptionOpenPath(pathname)) return null;
  return paymentPathFor(`${pathname}${search}${hash}`);
}

/**
 * What the paywall gate does on `pathname`, given the status query (CALL-04, 2026-10-26).
 *
 * It fails CLOSED. Until prod's paywall went on, a status call that errored rendered the
 * page ("fail open", BEN-141 QF4), because gating was off everywhere and the server
 * enforced it anyway. With gating on, that sent an unpaid account into the app shell (a
 * 429, a 500 or a timeout on /api/payments/status/ was enough), seen on prod as /home
 * staying on /home. Now an error after the query's retries lands on Pick your plan, which
 * offers "Continue" back to `next` as soon as it can read an active plan.
 */
export type SubscriptionGateDecision =
  | { kind: "render" }
  | { kind: "wait" }
  | { kind: "redirect"; to: string };

export function subscriptionGateDecision(
  location: { pathname: string; search: string; hash: string },
  query: { status: "pending" | "error" | "success"; data?: SubscriptionState },
): SubscriptionGateDecision {
  if (isSubscriptionOpenPath(location.pathname)) return { kind: "render" };
  if (query.status === "pending") return { kind: "wait" };
  if (query.status === "error" || !query.data) {
    return {
      kind: "redirect",
      to: paymentPathFor(`${location.pathname}${location.search}${location.hash}`),
    };
  }
  const to = subscriptionRedirectTarget(
    location.pathname,
    location.search,
    location.hash,
    query.data,
  );
  return to ? { kind: "redirect", to } : { kind: "render" };
}

/**
 * Where a 402 from any API should take the browser (CALL-04): Pick your plan, returning
 * here, unless the page is one an unpaid account may use (Settings, Help, Legal, Payment
 * itself). Null means stay. The server's 402 is the enforcement; this only makes sure the
 * screen agrees with it, whatever page fired the request.
 */
export function subscriptionRequiredRedirect(location: {
  pathname: string;
  search: string;
  hash: string;
}): string | null {
  if (isSubscriptionOpenPath(location.pathname)) return null;
  return paymentPathFor(`${location.pathname}${location.search}${location.hash}`);
}

/** The plan page, returning to `returnTo` (an internal path) once there is a plan. */
export function paymentPathFor(returnTo: string): string {
  return `/payment?next=${encodeURIComponent(returnTo)}`;
}

/** Accept only an internal absolute path before turning `next` back into a link. */
export function safeSubscriptionNext(value: string | null): string | null {
  if (!value || !value.startsWith("/") || value.startsWith("//")) return null;
  return value;
}

export function subscriptionNotice(status: SubscriptionStatus): SubscriptionNotice | null {
  switch (status) {
    case "none":
      return {
        title: "A subscription is required",
        body: "Choose a plan below to start your trial and continue to the page you requested.",
      };
    case "canceled":
    case "incomplete_expired":
      return {
        title: "Your subscription has ended",
        body: "Choose a plan below to restore access, or open the Stripe billing portal for billing history.",
      };
    case "past_due":
      return {
        title: "Your payment needs attention",
        body: "You still have access while Stripe retries the payment. Update your payment method to avoid an interruption.",
      };
    case "unpaid":
      return {
        title: "Your payment could not be completed",
        body: "Update your payment method in Stripe or choose a plan below to restore access.",
      };
    case "paused":
      return {
        title: "Your subscription is paused",
        body: "Open the Stripe billing portal to resume, or choose a plan below to restore access.",
      };
    case "incomplete":
      return {
        title: "Your subscription setup is incomplete",
        body: "Finish checkout or choose a plan below to continue.",
      };
    default:
      return null;
  }
}
